import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    AfterViewInit,
    NgZone,
    OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GoogleMapsModule, GoogleMap, MapMarker } from '@angular/google-maps';
import { BranchDetailDto, CreateBranchDto, UpdateBranchDto } from '../../../../core/data/new-dtos/bank.dtos';
import { RegionDto } from '../../../../core/data/new-dtos/region.dtos';

declare var google: any;

export interface BranchModalResult {
    action: 'save' | 'cancel';
    data?: CreateBranchDto | UpdateBranchDto;
}

@Component({
    selector: 'app-branch-modal',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        GoogleMapsModule,
    ],
    templateUrl: './branch-modal.component.html',
    styleUrls: ['./branch-modal.component.scss'],
})
export class BranchModalComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
    @ViewChild(GoogleMap) map!: GoogleMap;

    @Input() branch?: BranchDetailDto;
    @Input() isVisible = false;
    /** The bank's regions; empty hides the region select. */
    @Input() regions: RegionDto[] = [];

    @Output() closed = new EventEmitter<BranchModalResult>();

    form!: FormGroup;

    // Map configuration
    center: google.maps.LatLngLiteral = { lat: 18.4861, lng: -69.9312 }; // Santo Domingo
    zoom = 15;
    markerPosition: google.maps.LatLngLiteral = { lat: 18.4861, lng: -69.9312 };
    markerOptions: google.maps.MarkerOptions = {
        draggable: true,
    };
    mapOptions: google.maps.MapOptions = {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        restriction: {
            latLngBounds: {
                north: 20.0,
                south: 17.5,
                west: -72.0,
                east: -68.0,
            },
            strictBounds: false,
        },
    };

    private autocomplete: any;

    constructor(
        private fb: FormBuilder,
        private ngZone: NgZone,
        private elementRef: ElementRef,
    ) { }

    get isEditMode(): boolean {
        return !!this.branch;
    }

    ngOnInit() {
        this.form = this.fb.group({
            name: ['', Validators.required],
            latitude: [this.center.lat, Validators.required],
            longitude: [this.center.lng, Validators.required],
            regionId: [null as number | null],
        });

        if (this.branch) {
            this.form.patchValue({
                name: this.branch.name,
                latitude: this.branch.latitude,
                longitude: this.branch.longitude,
                regionId: this.branch.regionId ?? null,
            });
            this.markerPosition = { lat: this.branch.latitude, lng: this.branch.longitude };
            this.center = { ...this.markerPosition };
        }
    }

    ngAfterViewInit() {
        // Move modal to body to avoid z-index issues
        document.body.appendChild(this.elementRef.nativeElement);
        this.initAutocomplete();
    }

    ngOnDestroy() {
        // Remove from body when destroyed
        if (this.elementRef.nativeElement && this.elementRef.nativeElement.parentNode) {
            this.elementRef.nativeElement.parentNode.removeChild(this.elementRef.nativeElement);
        }
    }

    private async initAutocomplete() {
        if (!this.searchInput?.nativeElement) return;

        try {
            // Wait for google maps to be available and load places library
            if (typeof google === 'undefined' || !google.maps) {
                setTimeout(() => this.initAutocomplete(), 200);
                return;
            }

            // Use the new async loader to import the places library
            const { Autocomplete } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

            this.autocomplete = new Autocomplete(this.searchInput.nativeElement, {
                types: ['establishment', 'geocode'],
                componentRestrictions: { country: 'do' },
            });

            this.autocomplete.addListener('place_changed', () => {
                this.ngZone.run(() => {
                    const place = this.autocomplete.getPlace();

                    if (!place.geometry?.location) return;

                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();

                    this.markerPosition = { lat, lng };
                    this.center = { lat, lng };
                    this.updateCoordinates(lat, lng);
                });
            });
        } catch (error) {
            console.error('Error initializing autocomplete:', error);
        }
    }

    onMarkerDragEnd(event: google.maps.MapMouseEvent) {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            this.markerPosition = { lat, lng };
            this.updateCoordinates(lat, lng);
        }
    }

    private updateCoordinates(lat: number, lng: number) {
        this.form.patchValue({
            latitude: lat,
            longitude: lng,
        });
    }

    onSave() {
        if (this.form.invalid) return;

        const data: CreateBranchDto = {
            name: this.form.value.name,
            latitude: this.form.value.latitude,
            longitude: this.form.value.longitude,
            regionId: this.form.value.regionId != null ? Number(this.form.value.regionId) : null,
        };

        this.closed.emit({ action: 'save', data });
    }

    onCancel() {
        this.closed.emit({ action: 'cancel' });
    }

    onBackdropClick(event: MouseEvent) {
        if ((event.target as HTMLElement).classList.contains('modal')) {
            this.onCancel();
        }
    }
}
